// TypeScript model of incident_bundle.json — mirrors the spec §3 + §4a contract
// (doc/incident_lens_frontend_spec.md) and the design's makeBundle() shape.
//
// Fields the *enriched exporter* and the *demo bundle* provide are typed required;
// fields that only some bundles carry (device highlights, cross_outlet, outlet
// presets, reader-copy enrichment) are optional, and bundle.ts fills/degrades them.

export const NUMERIC_AXES = [
  "civ_agency", "civ_char", "off_agency", "off_char",
  "force_justification", "systemic_context", "sourcing_balance", "moral_lean",
] as const;
export type NumericAxis = (typeof NUMERIC_AXES)[number];

export type AgentOfForce = "officer_agent" | "passive_no_agent" | "civilian_blamed" | "mutual";
export type ClaimStance = "asserted_fact" | "attributed" | "contested";
export type HeadlinePerspective = "police" | "civilian" | "neutral";
export type FramePolarity = "threat" | "human" | "neutral";
export type HighlightKind = "axis" | "device" | "verbatim";

export interface Scores {
  civ_agency: number; civ_char: number; off_agency: number; off_char: number;
  force_justification: number; systemic_context: number;
  sourcing_balance: number; moral_lean: number;
  agent_of_force: AgentOfForce;
  police_claim_stance: ClaimStance;
  headline_perspective: HeadlinePerspective;
  relevant?: boolean;
  multiple_civilians?: boolean;
  [dev: string]: number | string | boolean | undefined; // dev_* booleans
}

export interface Incident {
  join_gva_id: number;
  city_and_state: string;
  date?: string;
  date_label?: string;        // "March 14, 2019" (enriched)
  civilian_names: string[];
  outlets: string[];
}

export interface Target {
  outlet: string;
  outlet_display: string;
  headline: string;
  body: string;
  scores?: Scores;
}

export interface PerOutletValue { outlet: string; value: number }

export interface AxisProfileEntry {
  axis: NumericAxis;
  label: string;
  target_value: number;
  midpoint: number;
  scale: [number, number];
  incident_mean: number;
  incident_range: number;
  per_outlet: PerOutletValue[];
  descriptive_text: string;
  // enriched / design fields (optional — bundle.ts fills from labels.ts when absent)
  lean_label?: string;
  evidence_quote?: string;
  evidence_kind?: HighlightKind;
  low?: string;
  high?: string;
  dim?: boolean;
}

export interface CategoricalChip {
  label: string;
  value: string;
  note: string;
}

export interface CrossOutlet {
  claim_id?: string;
  alt_text: string;
  alt_outlet: string;
}

export interface Highlight {
  id: string;
  kind: HighlightKind;
  ref: string;
  quote: string;
  explanation: string;
  char_start?: number;
  char_end?: number;
  cross_outlet?: CrossOutlet;
}

export interface FactClaim {
  claim_id: string;
  text: string;
  claim_type: string;
  asserted_by: string[];
  attribution_status: ClaimStance;
  corroborating_outlets: string[];
  contested_by: string[];
  frame_polarity: FramePolarity;
  verbatim_support: string;
}

export interface FactTable {
  source_types_present: string[];
  direct_quotes: string[];
  claims: FactClaim[];
  notes?: string;
}

export interface AxisChange {
  axis: string;
  fact_claim_ids: string[];
  before: string;
  after: string;
  why: string;
}

export type DiffOp =
  | { op: "equal"; text: string }
  | { op: "insert"; text: string; change_ref?: number }
  | { op: "delete"; text: string; change_ref?: number };

export interface Rewrite {
  no_op: boolean;
  rewritten_headline: string;
  rewritten_body: string;
  optional_context_block: string;
  change_log: AxisChange[];
  diff_ops: DiffOp[];
}

export interface Invariant {
  ok: boolean;
  quotes_preserved: boolean;
  facts_added: string[];
  facts_dropped: string[];
  quote_violations?: string[];
}

export interface ManipulationCheck {
  intended_direction: string;
  achieved_direction: string;
  axes_on_target: number;
  axes_total: number;
  landed: boolean;
  sign_agreement?: number;
}

export interface ReframeResult {
  profile_id: string;
  display?: string;
  as_read?: boolean;
  requested_target: Record<string, number>;
  structural_targets: Record<string, string>;
  rewritten_headline: string;
  rewritten_body: string;
  achieved_scores: Record<string, number | string | boolean>;
  residuals: Record<string, number>;
  change_log: AxisChange[];
  diff_ops?: DiffOp[];
  invariant: Invariant;
  manipulation_check: ManipulationCheck;
  // client-only annotations for the simulated/live path
  _custom?: boolean;
  _closest?: string;
}

export interface ReframeProfile {
  profile_id: string;
  kind: "pole" | "outlet" | "as_read";
  display: string;
  outlet_domain?: string | null;
  n_articles?: number | null;
  axis_means: Record<string, number>;
  device_rates: Record<string, number>;
}

export interface StructuralDef {
  label: string;
  options: string[];
  display: Record<string, string>;
}

export interface ReframeLiveConfig {
  endpoint: string;
  debounce_ms: number;
  session_call_cap: number;
  quantize: string;
}

export interface ReframeBlock {
  enabled: boolean;
  axes: NumericAxis[];
  axis_labels: Record<string, string>;
  axis_scales: Record<string, [number, number]>;
  structural: Record<string, StructuralDef>;
  default_profile_id: string;
  live: ReframeLiveConfig;
  profiles: ReframeProfile[];
  precomputed: Record<string, ReframeResult>;
}

// Full article from ANOTHER outlet covering the same incident — powers the in-app
// read-only source viewer. `outlet` is the outlet_domain and matches every other
// outlet reference in the bundle (cross_outlet.alt_outlet, corroborating_outlets, …).
export interface SourceArticle {
  outlet: string;          // outlet_domain, e.g. "miamiherald.com"
  outlet_display: string;  // human name
  headline: string;
  url: string;             // original article URL (may be dead — external link is secondary)
  date_published?: string; // raw string, may be missing
  body: string;            // full plain text of that outlet's article
}

export interface Bundle {
  schema_version: string;
  incident: Incident;
  target: Target;
  content_warning: string[];
  lean_summary: string;
  disclosure: string;
  axis_profile: AxisProfileEntry[];
  categoricals: CategoricalChip[];
  highlights: Highlight[];
  fact_table: FactTable;
  rewrite: Rewrite;
  reframe: ReframeBlock;
  sources?: SourceArticle[]; // per-outlet full articles (absent in older/demo bundles)
}

// ---- conditions ----
export type PanelId = "P1" | "P2" | "P3" | "P4" | "P5" | "P6" | "P7";
export type ConditionKey = "C1" | "C2" | "C3" | "C4" | "C5";

export interface ConditionDef {
  label: string;
  title: string;
  key: string; // telemetry condition string, e.g. "C4_full_lens"
  panels: PanelId[];
  desc: string;
}

// Follows the design's CONDS(); C5 is the exploratory reframe arm (leaner than spec §5).
export const CONDITIONS: Record<ConditionKey, ConditionDef> = {
  C1: { label: "C1", title: "Original (control)", key: "C1_original", panels: ["P1"],
        desc: "Control: the article exactly as published." },
  C2: { label: "C2", title: "Highlights (contrast without rewrite)", key: "C2_highlights",
        panels: ["P1", "P3"],
        desc: "Awareness via span highlights: framing devices marked in the text, with per-span explanations. No rewrite." },
  C3: { label: "C3", title: "Rewrite (debiased text only)", key: "C3_rewrite", panels: ["P1", "P5"],
        desc: "A neutralized rewrite shown as tracked changes. No provenance or contrast tools." },
  C4: { label: "C4", title: "Full lens (highlights, rewrite, context)", key: "C4_full_lens",
        panels: ["P1", "P3", "P5", "P6"],
        desc: "The full lens: span highlights, the neutral rewrite shown as tracked changes, and optional context." },
  C5: { label: "C5", title: "Ultimate (full lens + reframe what-if)", key: "C5_reframe",
        panels: ["P1", "P3", "P5", "P6", "P7"],
        desc: "Everything: span highlights, the switchable neutral-rewrite edit trace, context, plus the reframe what-if explorer. Facts stay fixed; framing varies." },
};
